/**
 * Account home screen — pure view-model helpers (no React, no side effects).
 */
import type { CustomerMembershipsDTO } from '../accountTypes.js';

export interface AccountHomeEntitlements {
  readonly promotionsEnabled: boolean;
  readonly loyaltyEnabled: boolean;
  readonly walletVisible: boolean;
}

export type AccountHomeScreenPhase = 'loading' | 'ready';

/**
 * Gates loyalty/promo UI on memberships module flags ∧ tenant entitlement blocks.
 * Missing/false blocks deny (never claim entitled when block DENY).
 */
export function resolveAccountHomeEntitlements(
  memberships: CustomerMembershipsDTO | undefined,
  blocks?: {
    readonly loyalty_program?: boolean;
    readonly promotions_program?: boolean;
  } | null,
): AccountHomeEntitlements {
  const loyaltyBlock = blocks?.loyalty_program === true;
  const promotionsBlock = blocks?.promotions_program === true;
  const promotionsEnabled =
    promotionsBlock && memberships?.promotions?.enabled === true;
  const loyaltyEnabled =
    loyaltyBlock &&
    (memberships?.loyalty?.enabled === true || memberships?.walletVisible === true);
  const walletVisible =
    loyaltyBlock &&
    (memberships?.loyalty?.walletVisible === true || memberships?.walletVisible === true);

  return {
    promotionsEnabled,
    loyaltyEnabled,
    walletVisible,
  };
}

export type PaymentClaimsAffordancePhase =
  | 'loading'
  | 'error'
  | 'disabled'
  | 'enabled';

export interface PaymentClaimsAffordance {
  readonly showCta: boolean;
  readonly phase: PaymentClaimsAffordancePhase;
}

/**
 * Customer payment-claim gate from a **successful** memberships DTO.
 *
 * - No DTO (`undefined`) → false (fail-closed; do not treat load/error as enabled).
 * - Successful DTO with missing `paymentClaims` → true (legacy API; tenant default ADMIN_APPROVAL).
 * - Explicit `paymentClaims.enabled: false` → false (DISABLED mode).
 *
 * Prefer {@link resolvePaymentClaimsAffordance} when query status is available.
 */
export function resolvePaymentClaimsEnabled(
  memberships: CustomerMembershipsDTO | undefined,
): boolean {
  if (memberships === undefined) {
    return false;
  }
  if (memberships.paymentClaims === undefined) {
    // Legacy successful response without paymentClaims — default enabled.
    return true;
  }
  return memberships.paymentClaims.enabled === true;
}

/**
 * Fail-closed payment-claim affordance from memberships query status + data.
 * Loading / error / missing usable DTO never equal enabled CTA.
 */
export function resolvePaymentClaimsAffordance(input: {
  readonly data: CustomerMembershipsDTO | undefined;
  readonly isLoading?: boolean;
  readonly isPending?: boolean;
  readonly isError?: boolean;
}): PaymentClaimsAffordance {
  const awaitingFirstLoad =
    input.isLoading === true ||
    (input.isPending === true && input.data === undefined && input.isError !== true);

  if (awaitingFirstLoad) {
    return { showCta: false, phase: 'loading' };
  }

  if (input.isError === true || input.data === undefined) {
    return { showCta: false, phase: 'error' };
  }

  if (!resolvePaymentClaimsEnabled(input.data)) {
    return { showCta: false, phase: 'disabled' };
  }

  return { showCta: true, phase: 'enabled' };
}

export function resolveAccountHomePhase(input: {
  readonly isMeLoading: boolean;
  readonly isEntitlementsLoading?: boolean;
}): AccountHomeScreenPhase {
  if (input.isMeLoading || input.isEntitlementsLoading === true) {
    return 'loading';
  }
  return 'ready';
}

/** True when account home will mount at least one loyalty/promo card (not null). */
export function resolveAccountHomeHasRelationshipCards(
  entitlements: AccountHomeEntitlements,
): boolean {
  return (
    entitlements.promotionsEnabled ||
    entitlements.loyaltyEnabled ||
    entitlements.walletVisible
  );
}
